/**
 * Shared amenity resolver. Each provider owns its own code → label map
 * (`providers/amadeus/resources/amenities.js`,
 * `providers/kyte/resources/amenities.js`); this module merges them so
 * consumers don't have to know which provider produced the amenity.
 * Codes don't collide today because Amadeus uses GDS-style 3-letter /
 * numeric codes and Kyte uses a `KYTE_` prefix.
 */

import { AMADEUS_AMENITIES } from '@liveflight/providers/amadeus/resources/amenities';
import { KYTE_AMENITIES } from '@liveflight/providers/kyte/resources/amenities';

export const amenities = {
  ...AMADEUS_AMENITIES,
  ...KYTE_AMENITIES,
};

// Order matters: HAND_BAG / personal-item patterns must sit above the
// broader cabin-bag bucket so "handbag" doesn't fall into the trolley
// branch first. Tested against `code + description`.
const ICON_PATTERNS = [
  {
    match:
      /\b(HAND_BAG|hand ?bag|handbag|personal item|small bag|under the seat|zaino|backpack|borsa|oggetto personale)\b/i,
    icon: 'backpack',
  },
  {
    match:
      /\b(CABIN_BAG|trolley|cabin bag|carry[- ]?on|overhead|locker|bagaglio a mano|bagaglio in cabina)\b/i,
    icon: 'trolley',
  },
  {
    match:
      /\b(CHECKED_BAG|luggage|stiva|checked bag|check-?in bag|hold bag|bag max|bag up to|valigia|suitcase|\d+st bag|\d+nd bag|\d+rd bag|\d+th bag|\d+\s?kg)\b/i,
    icon: 'luggage',
  },
  {
    match:
      /\b(seat|reserved seat|posto|posti|poltrona|armchair|lie[- ]?flat)\b/i,
    icon: 'armchair',
  },
  {
    match:
      /\b(meal|pasto|pranzo|cibo|food|catering|dining|snack|breakfast|bar service)\b/i,
    icon: 'restaurant',
  },
  { match: /\b(refund|rimborso|rimborsabile|refundable)\b/i, icon: 'wallet' },
  {
    match: /\b(change|modifica|cambio|changeable|modificabile)\b/i,
    icon: 'pen',
  },
  {
    match:
      /\b(priorità|prioritari[oa]|priority|fast track|sky priority|dedicated check[- ]?in|priority boarding)\b/i,
    icon: 'star',
  },
  {
    match:
      /\b(miles?|miglia|mileage|points?|punti|loyalty|volare|flying blue|skymiles|avios)\b/i,
    icon: 'star',
  },
  { match: /\b(lounge)\b/i, icon: 'star' },
];

/**
 * Match an amenity's code + description against ICON_PATTERNS to derive
 * an icon name; falls back to `check_circle` when no pattern matches.
 * @param {{code?: string, description?: string}} amenity
 * @returns {string}
 */
export function infer_icon(amenity) {
  if (!amenity) return 'check_circle';
  const haystack =
    `${amenity.code || ''} ${amenity.description || ''}`.toLowerCase();
  if (!haystack.trim()) return 'check_circle';
  const hit = ICON_PATTERNS.find((p) => p.match.test(haystack));
  return hit ? hit.icon : 'check_circle';
}

/**
 * Resolve a normalized amenity into the `{ txt, icon, tooltip, description }`
 * shape consumed by `branded-fare-amenity.vue`. Provider-curated entries
 * win; when the code has no entry the raw description becomes the label
 * and the icon falls back to regex inference on the description.
 * @param {{code?: string, description?: string, isChargeable?: boolean}} amenity
 * @returns {{txt: string, icon: string, tooltip: string, description: string}}
 */
export function get_amenity_info(amenity) {
  const filtered = amenities[amenity.code] || {};
  return {
    ...(amenity || {}),
    ...filtered,
    txt: filtered.txt || amenity.description,
    icon: filtered.icon || infer_icon(amenity),
    tooltip: filtered.tooltip || amenity.tooltip || '',
    description: filtered.description || '',
  };
}
